import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "", code: "", is_default: false, is_active: true,
  address: { line1: "", city: "", state: "", pincode: "" },
  contact: { phone: "", email: "" },
};

export default function Warehouses() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try { const { data } = await api.get("/warehouses?limit=100"); setRows(data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...emptyForm, ...r,
      address: { ...emptyForm.address, ...(r.address || {}) },
      contact: { ...emptyForm.contact, ...(r.contact || {}) },
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/warehouses/${editing.id}`, form);
      else await api.post("/warehouses", form);
      toast.success(editing ? "Warehouse updated" : "Warehouse created");
      setOpen(false); fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/warehouses/${r.id}`); toast.success("Deleted"); fetchRows(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "name", header: "Warehouse", render: (r) => (
      <div>
        <div className="flex items-center gap-2">
          <span className="font-medium">{r.name}</span>
          {r.is_default && <Badge className="bg-accent hover:bg-accent">Default</Badge>}
        </div>
        <div className="text-xs text-muted-foreground font-mono">{r.code || "—"}</div>
      </div>
    )},
    { key: "address", header: "Location", render: (r) => `${r.address?.city || "—"}, ${r.address?.state || ""}` },
    { key: "contact", header: "Phone", render: (r) => r.contact?.phone || "—" },
    { key: "is_active", header: "Status", className: "w-24",
      render: (r) => <Badge variant={r.is_active ? "default" : "secondary"} className={r.is_active ? "bg-accent hover:bg-accent" : ""}>{r.is_active ? "Active" : "Inactive"}</Badge> },
    { key: "actions", header: "", className: "w-24 text-right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(r); }} data-testid={`edit-wh-${r.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`delete-wh-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
        </div>
      )},
  ];

  return (
    <div className="p-8" data-testid="warehouses-page">
      <PageHeader title="Warehouses" subtitle="Fulfilment locations"
        actions={<Button onClick={openCreate} className="btn-primary-glow" data-testid="add-wh-btn"><Plus className="w-4 h-4 mr-2" />New warehouse</Button>} />
      <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openEdit} testId="warehouses-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="warehouse-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit warehouse" : "New warehouse"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wh-name" /></div>
            <div><Label>Code</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="auto" /></div>
            <div><Label>Phone</Label><Input value={form.contact.phone} onChange={(e) => setForm({ ...form, contact: { ...form.contact, phone: e.target.value } })} /></div>
            <div className="col-span-2"><Label>Address line 1</Label><Input value={form.address.line1} onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value } })} /></div>
            <div><Label>City</Label><Input value={form.address.city} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} /></div>
            <div><Label>State</Label><Input value={form.address.state} onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })} /></div>
            <div><Label>Pincode</Label><Input value={form.address.pincode} onChange={(e) => setForm({ ...form, address: { ...form.address, pincode: e.target.value } })} /></div>
            <div className="flex items-center justify-between rounded-lg border p-3 col-span-2">
              <div><div className="text-sm font-medium">Default warehouse</div><div className="text-xs text-muted-foreground">Used for POS fulfilment by default</div></div>
              <Switch checked={!!form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="wh-default" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="btn-primary-glow" data-testid="save-wh-btn">{saving ? "Saving…" : editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
